import { supabase } from '@/lib/supabase';

// ─── Unified Plan Configuration (single source of truth) ────────────
export interface PlanInfo {
  id: string;
  nome: string;
  preco: number;
  descricao: string;
  condominiosMax: number;
  usuariosMax: number;
  recursos: string[];
  destaque: boolean;
  popular: boolean;
}

// ─── Hardcoded fallback (used before DB loads or when DB is unavailable).
// Nunca e misturado ao catalogo do banco depois que ele carrega.
const FALLBACK: Record<string, PlanInfo> = {
  'plano-essencial': {
    id: 'plano-essencial',
    nome: 'Essencial',
    preco: 29,
    descricao: 'Perfeito para síndicos que estão começando a digitalizar a gestão.',
    condominiosMax: 1,
    usuariosMax: 1,
    recursos: [
      'Dashboard com KPIs',
      'Gestão de 1 condomínio',
      'Cadastro de torres e imóveis',
      'Infraestrutura básica',
      'Corpo de bombeiros',
      'Anexos (até 50 arquivos)',
    ],
    destaque: false,
    popular: false,
  },
  'plano-profissional': {
    id: 'plano-profissional',
    nome: 'Profissional',
    preco: 149,
    descricao: 'Ideal para administradoras e síndicos com múltiplos condomínios.',
    condominiosMax: 10,
    usuariosMax: 5,
    recursos: [
      'Dashboard com KPIs',
      'Gestão de até 10 condomínios',
      'Cadastro de torres e imóveis',
      'Infraestrutura e manutenções',
      'Corpo de bombeiros e licenças',
      'Funcionários e anexos',
      'Relatórios exportáveis',
      'Suporte prioritário',
    ],
    destaque: true,
    popular: true,
  },
  'plano-enterprise': {
    id: 'plano-enterprise',
    nome: 'Enterprise',
    preco: 399,
    descricao: 'Para grandes administradoras com necessidades avançadas de gestão.',
    condominiosMax: 99,
    usuariosMax: 20,
    recursos: [
      'Tudo do Profissional',
      'Condomínios ilimitados',
      '20 usuários da equipe',
      'API de integração',
      'White label',
      'Treinamento dedicado',
      'Gerente de conta',
      'SLA premium',
    ],
    destaque: false,
    popular: false,
  },
};

// ─── Mutable runtime config — starts as fallback, replaced by DB on load
let _PlanConfig: Record<string, PlanInfo> = { ...FALLBACK };
let _planosDisponiveis: PlanInfo[] = Object.values(_PlanConfig);
let _dbLoaded = false;

// ─── Exported "live" references — components read these directly
export let PlanConfig: Record<string, PlanInfo> = _PlanConfig;
export let planosDisponiveis: PlanInfo[] = _planosDisponiveis;

// ─── Map DB row → PlanInfo
function dbRowToPlanInfo(row: any): PlanInfo {
  return {
    id: row.id,
    nome: row.nome,
    preco: (row.preco || 0) / 100,
    descricao: row.descricao || '',
    condominiosMax: row.condominios_max ?? 1,
    usuariosMax: row.usuarios_max ?? 1,
    recursos: row.recursos || [],
    destaque: row.destaque ?? false,
    popular: row.popular ?? false,
  };
}

// ─── Fetch from DB and update live references
//
// Devolve a lista, e nao `void`. As referencias exportadas sao variaveis de
// modulo: atualiza-las nao avisa o React de nada, entao a tela seguia
// desenhando os planos do FALLBACK ate um estado qualquer mudar — na pratica,
// ate a pessoa clicar num plano, quando os demais apareciam de uma vez. Quem
// chama guarda o retorno em estado e o render acompanha.
export async function fetchPlanosFromDB(): Promise<PlanInfo[]> {
  if (_dbLoaded) return _planosDisponiveis;
  try {
    // Configuracao vem da funcao `config`, que serve do cache (Redis) e so vai
    // ao Postgres quando o cache esta frio. Ler a tabela direto daqui
    // funcionaria, mas ignoraria o cache e transformaria cada navegacao em uma
    // consulta. Ver docs/architecture/cache.md.
    const { data, error } = await supabase.functions.invoke('config?chave=planos', {
      method: 'GET',
    });

    if (error || !Array.isArray(data) || data.length === 0) return _planosDisponiveis;

    // Com o catalogo carregado, a lista e exatamente a do banco — o FALLBACK
    // nao completa o que falta. Completar exibia planos que nao existem em
    // `planos`: a pessoa escolhia o plano e o `create-checkout-session`, que le
    // o mesmo catalogo, recusava com "Plano invalido ou indisponivel"
    // (P-42, ADR-0011).
    const config: Record<string, PlanInfo> = {};
    for (const row of data) {
      config[row.id] = dbRowToPlanInfo(row);
    }

    _PlanConfig = config;
    _planosDisponiveis = Object.values(config);
    PlanConfig = _PlanConfig;
    planosDisponiveis = _planosDisponiveis;
    _dbLoaded = true;
  } catch {
    // Keep fallback
  }

  return _planosDisponiveis;
}

export function getPlanById(id: string | null | undefined): PlanInfo | undefined {
  if (!id) return undefined;
  return _PlanConfig[id];
}

/**
 * Estado da assinatura como a tela de plano exibe.
 *
 * `cancel_at_period_end` não é status do Stripe, é um flag: a assinatura segue
 * `active` até o fim do período pago, e é assim que o perfil a guarda (P-50).
 * Só a exibição junta os dois em "Cancelamento agendado" — quem decide acesso
 * (`acessoDaConta`) lê o status cru.
 */
export function estadoExibidoDaAssinatura(
  status: string | null | undefined,
  cancelamentoAgendado: boolean | null | undefined,
): string {
  if (status === 'active' && cancelamentoAgendado) return 'cancel_at_period_end';
  return status || 'inactive';
}

// ─── Low-level DB fallback (bypasses edge function entirely) ─────────
export interface SubscriptionFallback {
  planId: string | null;
  status: string;
  stripeSubscriptionId: string | null;
  stripeCustomerId: string | null;
  condominiosMax: number;
  usuariosMax: number;
  condominiosAtual: number;
  usuariosAtual: number;
  planName: string;
  planPrice: number;
}

export async function loadSubscriptionFallback(): Promise<SubscriptionFallback | null> {
  try {
    let userId: string | undefined;
    const { data: sessionData } = await supabase.auth.getSession();
    userId = sessionData.session?.user?.id;

    if (!userId) {
      const { data: userData } = await supabase.auth.getUser();
      userId = userData.user?.id;
    }

    if (!userId) return null;

    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select(
        'id, account_id, plano_id, subscription_status, stripe_customer_id, stripe_subscription_id',
      )
      .eq('id', userId)
      .maybeSingle();

    if (profileError || !profile) return null;

    let condominiosCount = 0;
    if (profile.account_id) {
      try {
        const { count } = await supabase
          .from('condominios')
          .select('*', { count: 'exact', head: true })
          .eq('account_id', profile.account_id);
        condominiosCount = count || 0;
      } catch {
        /* ignore */
      }
    }

    let usuariosCount = 0;
    if (profile.account_id) {
      try {
        const { count } = await supabase
          .from('profiles')
          .select('*', { count: 'exact', head: true })
          .eq('account_id', profile.account_id);
        usuariosCount = count || 0;
      } catch {
        /* ignore */
      }
    }

    // Try to get plan from DB first, fallback to local config
    let plan = _PlanConfig[profile.plano_id || ''] || null;
    if (!plan && profile.plano_id) {
      // Fetch single plan from DB
      try {
        const { data: planRow } = await supabase
          .from('planos')
          .select('*')
          .eq('id', profile.plano_id)
          .maybeSingle();
        if (planRow) {
          plan = dbRowToPlanInfo(planRow);
          _PlanConfig[planRow.id] = plan;
        }
      } catch {
        /* ignore */
      }
    }

    return {
      planId: profile.plano_id || null,
      status: profile.subscription_status || 'inactive',
      stripeSubscriptionId: profile.stripe_subscription_id || null,
      stripeCustomerId: profile.stripe_customer_id || null,
      condominiosMax: plan?.condominiosMax || 0,
      usuariosMax: plan?.usuariosMax || 0,
      condominiosAtual: condominiosCount,
      usuariosAtual: usuariosCount,
      planName: plan?.nome || 'Desconhecido',
      planPrice: plan?.preco || 0,
    };
  } catch {
    return null;
  }
}
